/**
 * 案件起票モーダルの完了画面。
 * ProjectIntakeModal から分離した表示専用コンポーネント。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 起票完了後に Backlog / Slack / 案件仕様書の各リンクを一覧表示する
 * @graph-connects none
 */

import type { ReactNode } from 'react';
import type { CreateProjectResponse } from '../../../types/project-intake.js';

/** @graph-connects none */
export interface ProjectIntakeResultProps {
  /** 起票結果（各リソースの URL を含む） */
  result: CreateProjectResponse;
}

/**
 * 起票完了画面。各リソースへのリンクを縦に並べる。
 * @graph-connects none
 */
export function ProjectIntakeResult({ result }: ProjectIntakeResultProps): ReactNode {
  const links: { label: string; url: string }[] = [
    { label: `Backlog チケット（${result.backlogKey}）`, url: result.backlogUrl },
    { label: 'Slack チャンネル', url: result.slackChannelUrl },
    { label: '案件仕様書', url: result.specSheetUrl },
  ];
  return (
    <div className="space-y-3">
      {result.dryRun ? (
        <div className="rounded-md border border-[var(--color-warning)] bg-[var(--color-bg-elevated)] px-3 py-2">
          <p className="text-sm font-medium text-[var(--color-warning)]">
            ローカル dry-run のため、実際のリソースは作成されていません。
          </p>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            以下はダミーのリンク（#dry-run-*）です。実際に起票するには dry-run
            を無効化してください。
          </p>
        </div>
      ) : (
        <p className="text-sm text-[var(--color-text-secondary)]">
          案件を起票しました。以下のリンクから各リソースを確認できます。
        </p>
      )}
      <ul className="space-y-2">
        {links.map((l) =>
          result.dryRun ? (
            <li
              key={l.label}
              className="text-sm font-medium text-[var(--color-text-muted)] line-through"
            >
              {l.label}
            </li>
          ) : (
            <li key={l.label}>
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-[var(--color-accent-primary)] hover:underline"
              >
                {l.label} ↗
              </a>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}
